<template>
  <validation-observer ref="AddEquipmentForm" v-slot="{ invalid, errors }">
    <b-form @submit.prevent="addEquipment">
      <b-row>
        <!-- Equipment Code -->
        <b-col md="6">
          <b-form-group label="Equipment Code" label-for="mc-first-name">
            <validation-provider name="Equipment Code" rules="required" v-slot="{ errors }">
              <b-form-input
                id="mc-first-name"
                placeholder="Equipment Code"
                v-model="assetCode"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Equipment Name -->
        <b-col md="6">
          <b-form-group label="Equipment Name" label-for="mc-last-name">
            <validation-provider name="Equipment Name" rules="required" v-slot="{ errors }">
              <b-form-input
                id="mc-last-name"
                placeholder="Equipment Name"
                v-model="name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Equipment Description -->
        <b-col md="12">
          <b-form-group label="Equipment Description" label-for="mc-city">
            <b-form-textarea
              id="textarea-default"
              placeholder="Equipment Description"
              rows="3"
              v-model="description"
            />
          </b-form-group>
        </b-col>
        <!-- Supplier -->
        <b-col cols="6">
          <b-form-group label="Supplier" label-for="mc-Supplier">
            <b-form-select
              v-model="supplierId"
              :options="supplierList"
              value-field="id"
              text-field="name"
            />
          </b-form-group>
        </b-col>
        <!-- Staff -->
        <b-col cols="6">
          <b-form-group label="Staff" label-for="mc-Staff">
            <b-form-select
              v-model="staffId"
              :options="staffList"
              value-field="id"
              text-field="firstName"
            />
          </b-form-group>
        </b-col>
        <!-- Purchase Date -->
        <b-col md="12">
          <b-form-group label="Purchase Date" label-for="mc-purchase">
            <validation-provider name="Purchase Date" rules="required" v-slot="{ errors }">
              <b-form-datepicker
                id="example-purchase"
                class="mb-1"
                v-model="purchaseDate"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Expiry Date -->
        <b-col md="12">
          <b-form-group label="Expiry Date" label-for="mc-expiry">
            <validation-provider
              name="Expiry Date"
              :rules="{ notBeforePurchase: purchaseDate }"
              v-slot="{ errors }"
            >
              <b-form-datepicker
                id="example-expiry"
                v-model="lifeEndDate"
                class="mb-1"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Maintenance Date -->
        <b-col md="12">
          <b-form-group label="Maintenance Date" label-for="mc-Maintenance">
            <validation-provider
              name="Maintenance Date"
              :rules="{ notBeforePurchase: purchaseDate }"
              v-slot="{ errors }"
            >
              <b-form-datepicker
                id="example-Maintenance"
                v-model="maintainanceDate"
                class="mb-1"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Location -->
        <b-col md="6">
          <b-form-group label="Location" label-for="mc-last-name">
            <b-form-input
              id="mc-last-name"
              placeholder="Location"
              v-model="location"
            />
          </b-form-group>
        </b-col>
        <!-- Availability -->
        <b-col md="6">
          <b-form-group label="Availability" label-for="mc-last-name">
            <b-form-select v-model="selected" :options="optionsDLL" />
          </b-form-group>
        </b-col>
        <!-- Cost Per Hour -->
        <b-col md="6">
          <b-form-group label="Cost Per Hour">
            <validation-provider name="Cost Per Hour" rules="nonNegative" v-slot="{ errors }">
              <b-form-input
                type="number"
                placeholder="Cost / Hour (RS)"
                v-model="costPerHour"
                step="0.01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Submit Button -->
        <b-col md="12" class="d-flex justify-content-between">
          <b-button type="submit" variant="primary" :disabled="loading">
            Submit
            <b-spinner v-if="loading" class="mx-2" />
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormDatepicker,
  BFormGroup,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required } from "@validations"; // your pre-defined validation rules

// Custom rule: Ensures the selected date is not before the Purchase Date.
extend("notBeforePurchase", {
  params: ["purchaseDate"],
  validate(value, { purchaseDate }) {
    // If no purchase date is set, skip this validation (or you can return false to require it)
    if (!purchaseDate) return true;
    return new Date(value) >= new Date(purchaseDate);
  },
  message: "The date cannot be before the Purchase Date."
});

// Custom rule: Ensures the cost is non-negative.
extend("nonNegative", {
  validate(value) {
    return parseFloat(value) >= 1;
  },
  message: "The cost should be positive number."
});

import { mapActions, mapGetters } from "vuex";
import { fnSortingDropdown, customSwal } from "@/libs/customFn";

export default {
  name: "AddEquipment",
  components: {
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormDatepicker,
    BFormGroup,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      assetCode: "",
      loading: false,
      name: "",
      description: "",
      purchaseDate: null,
      supplierId: null,
      supplierList: [],
      staffId: null,
      staffList: [],
      lifeEndDate: null,
      maintainanceDate: null,
      location: "",
      selected: 5,
      costPerHour: 0,
      options: [
        { value: 1, text: "BOOKED" },
        { value: 2, text: "AVAILABLE" },
        { value: 3, text: "MAINTENANCE" },
        { value: 4, text: "BROKEN" },
        { value: 5, text: "NOT_AVAILABLE" },
      ],
    };
  },
  computed: {
    ...mapGetters("equipmentModule", {
      loading: "loading",
    }),
    ...mapGetters("supplierModule", {
      suppliers: "suppliers",
      loading: "loading",
    }),
    optionsDLL() {
      return fnSortingDropdown(this.options);
    },
    ...mapGetters("userModule", {
      staffOptions: "users",
      loading: "loading",
    }),
  },
  async mounted() {
    await this.getSupplierListAction();
    await this.getUserListAction();
    this.supplierList = fnSortingDropdown(this.suppliers, "asc", "name");
    this.staffList = fnSortingDropdown(this.staffOptions, "asc", "firstName");
  },
  methods: {
    success() {
      this.$swal({
        title: "Equipment!",
        text: "You have successfully added the equipment!",
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    // error handling
    error(data) {
      let msg = data ? this.getErrorString(data) : null;
      customSwal("One or more validation errors occurred.", "Error", "error");
    },
    getErrorString(data) {
      let msg = "";
      if (data.errors) {
        msg = "<div><p>Cannot post your request due to the following errors:</p>";
        for (let prop in data.errors) {
          if (
            Object.prototype.hasOwnProperty.call(data.errors, prop) &&
            prop !== "request"
          ) {
            msg += `<p>${prop.replace("$.", "")}: ${data.errors[prop]
              .toString()
              .replace("$.", "")}</p>`;
          }
        }
        msg += "</div>";
      } else if (data.message) {
        msg = `<div><p>Cannot post your request due to the following errors:</p>${data.message}</div>`;
      }
      return msg;
    },
    ...mapActions("equipmentModule", ["addEquipmentAction"]),
    ...mapActions("supplierModule", ["getSupplierListAction"]),
    ...mapActions("userModule", ["getUserListAction"]),
    addEquipment() {
      this.$refs.AddEquipmentForm.validate().then((success) => {
        if (success) {
          this.loading = true;
          const payload = {
            assetCode: this.assetCode,
            purchaseDate: this.purchaseDate,
            name: this.name,
            description: this.description,
            lifeEndDate: this.lifeEndDate,
            maintainanceDate: this.maintainanceDate,
            location: this.location,
            isActive: true,
            status: this.selected,
            costPerHour: this.costPerHour,
            supplierId: this.supplierId,
            staffId: this.staffId,
          };
          this.addEquipmentAction(payload)
            .then(() => {
              this.success();
              this.$emit("refresh");
              this.$bvModal.hide("modal-main");
              this.loading = false;
            })
            .catch((error) => {
              this.error(error.response.data);
              this.loading = false;
            });
        }
      });
    },
  },
};
</script>
